import { useCallback, useRef } from "react";

/** Ref-backed store with a useSyncExternalStore-compatible API.
 *  Updates bypass React state to avoid re-renders on every pointermove. */
export function useLiveStore<T>(equals?: (a: T, b: T) => boolean) {
    const valueRef = useRef<T | null>(null);
    const listenersRef = useRef<Set<() => void>>(new Set());
    const equalsRef = useRef(equals);
    equalsRef.current = equals;

    const notify = useCallback(() => {
        for (const listener of listenersRef.current) {
            listener();
        }
    }, []);

    const set = useCallback(
        (value: T | null) => {
            const prev = valueRef.current;
            if (prev === value) {
                return;
            }
            if (prev && value && equalsRef.current?.(prev, value)) {
                return;
            }
            valueRef.current = value;
            notify();
        },
        [notify],
    );

    const get = useCallback(() => valueRef.current, []);

    const subscribe = useCallback((listener: () => void) => {
        listenersRef.current.add(listener);
        return () => {
            listenersRef.current.delete(listener);
        };
    }, []);

    return { set, get, subscribe };
}
